const API_BASE_URL = '';

interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
}

async function apiRequest<T>(endpoint: string, body: Record<string, unknown>): Promise<ApiResponse<T>> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Request failed with status ${response.status}`);
  }

  return response.json();
}

export async function resetPassword(email: string): Promise<void> {
  try {
    await apiRequest('/api/v2/request_new_password', { email });
  } catch {
    throw new Error('There is no account associated with the provided email.');
  }
}

export async function submitNewPassword(
  password: string,
  passwordConfirmation: string,
  resetPasswordToken: string
): Promise<void> {
  try {
    await apiRequest('/api/v2/set_new_password', {
      password,
      password_confirmation: passwordConfirmation,
      reset_password_token: resetPasswordToken,
    });
  } catch {
    throw new Error('There is an error setting the new password.');
  }
}

export async function resendConfirmation(email: string): Promise<void> {
  try {
    await apiRequest('/api/v2/confirmation_instructions', { email });
  } catch {
    throw new Error('There is no account awaiting confirmation with the provided email.');
  }
}

export async function resendUnlock(email: string): Promise<void> {
  try {
    await apiRequest('/api/v2/unlock_instructions', { email });
  } catch {
    throw new Error('There is no locked account with the provided email.');
  }
}

export async function requestMagicLink(email: string, recaptchaToken: string, accessLinkToken?: string): Promise<void> {
  try {
    await apiRequest('/api/v2/magic_link_request', {
      email,
      'g-recaptcha-response-data': recaptchaToken,
      access_link_token: accessLinkToken,
    });
  } catch {
    throw new Error('Unable to send sign-in link. Please try again.');
  }
}

export async function createEventMagicLink(email: string, token: string): Promise<void> {
  try {
    await apiRequest(`/api/v2/events/${token}/auth`, { email });
  } catch {
    throw new Error('Invalid token or event');
  }
}

export async function requestAccess(
  firstName: string,
  lastName: string,
  email: string,
  recaptchaToken: string
): Promise<void> {
  try {
    await apiRequest('/api/v2/request_access_web', {
      first_name: firstName,
      last_name: lastName,
      email,
      'g-recaptcha-response-data': recaptchaToken,
    });
  } catch {
    throw new Error('Unable to submit access request. Please try again.');
  }
}

export async function confirmGuestUser(
  confirmationToken: string,
  password: string,
  passwordConfirmation: string
): Promise<void> {
  try {
    await apiRequest('/api/v2/guests/confirm', {
      confirmation_token: confirmationToken,
      password,
      password_confirmation: passwordConfirmation,
    });
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unable to confirm account. Please try again.');
  }
}
